import React from 'react';
import { View, ScrollView, StyleSheet } from 'react-native';
import { Text } from '@/components/common/text';
import { StackScreenProps } from '@react-navigation/stack';

import { AdminStackParamList } from '@/navigation/admin/AdminNavigator';
import { BaseAdminScreen } from '@/components';
import { ScreenBackLink } from '@/components/residents/mobile/ScreenBackLink';
import { useThemedStyles } from '@/hooks/shared/useThemeColors';

type EditPropertyScreenProps = StackScreenProps<AdminStackParamList, 'EditProperty'>;

export const EditPropertyScreen: React.FC<EditPropertyScreenProps> = ({
  navigation,
  route,
}) => {
  const { property } = route.params;
  const styles = useStyles();

  const renderField = (label: string, value: string | number | undefined | null) => {
    if (value === undefined || value === null || value === '') return null;
    return (
      <View style={styles.field}>
        <Text style={styles.label}>{label}</Text>
        <Text style={styles.value}>{String(value)}</Text>
      </View>
    );
  };

  return (
    <BaseAdminScreen
      screenName="EditProperty"
      navigation={navigation}
      showPlaceholder={false}
      title={property.marketingName}
      scrollEnabled={false}
      contentContainerStyle={styles.baseScreenContent}
    >
      <ScrollView
        style={styles.container}
        contentContainerStyle={styles.contentInner}
        showsVerticalScrollIndicator={false}
      >
        <ScreenBackLink style={styles.backLink} />
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>General Information</Text>
          {renderField('Name', property.marketingName)}
          {renderField('External ID', property.externalId)}
          {renderField('Address', property.address)}
          {renderField('Organization ID', property.organizationId)}
          {renderField('PMS Type', property.pmsType || 'Yardi')}
        </View>

        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Contact Information</Text>
          {renderField('Email', property.propertyEmailAddress || '—')}
          {renderField('Phone', property.propertyPhoneNumber || '—')}
          {renderField('Website', property.propertyUrl)}
        </View>

        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Fee Configuration</Text>
          {renderField('NSF Fee', `$${property.propertyNsfFee || 0}`)}
          {property.propertyLateDayStart !== undefined &&
            renderField('Late Day Start', `Day ${property.propertyLateDayStart}`)}
          {renderField('Late Day Fee', `$${property.propertyLateDayFee || 0}`)}
          {renderField('Late Daily Fee', `$${property.propertyLateDailyFee || 0}`)}
          {renderField('Max Monthly Fee', `$${property.propertyMaxMonthlyFee || 0}`)}
        </View>

        {(property.creditCardMerchantAccountId || property.achMerchantAccountId) && (
          <View style={styles.section}>
            <Text style={styles.sectionTitle}>Merchant Accounts</Text>
            {renderField('Credit Card Merchant ID', property.creditCardMerchantAccountId)}
            {renderField('ACH Merchant ID', property.achMerchantAccountId)}
          </View>
        )}
      </ScrollView>
    </BaseAdminScreen>
  );
};

const useStyles = () =>
  useThemedStyles(({ colors, components }) =>
    StyleSheet.create({
      baseScreenContent: {
        paddingHorizontal: 0,
        paddingTop: 0,
        paddingBottom: 0,
        flexGrow: 1,
      },
      container: {
        flex: 1,
      },
      contentInner: {
        paddingHorizontal: 16,
        paddingBottom: 32,
      },
      backLink: {
        marginTop: 8,
        marginBottom: -4,
      },
      section: {
        marginBottom: 32,
      },
      sectionTitle: {
        fontSize: 14,
        fontWeight: '600',
        color: colors.text.primary,
        marginBottom: 16,
        textTransform: 'uppercase',
        letterSpacing: 0.5,
        marginTop: 16,
      },
      field: {
        marginBottom: 16,
      },
      label: {
        fontSize: 12,
        fontWeight: '500',
        color: components.admin.formInput.label,
        marginBottom: 4,
      },
      value: {
        fontSize: 14,
        color: colors.text.primary,
        lineHeight: 20,
      },
    }),
  );
